import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import {
  addSubscriptionUpsellProduct,
  getSubscriptionUpsells
} from "app/subscriptions/actions";
import ErrorMessageModal from "shared/modals/ErrorMessageModal";

import SubscriptionUpsellCard from "./SubscriptionUpsellCard";
import styles from "./SubscriptionUpsells.module.scss";

const ELIGIBLE_STATUSES = ["active", "paused", "failing", "failed"];

const SubscriptionUpsells = ({ subscription }) => {
  const dispatch = useDispatch();
  const [upsells, setUpsells] = useState(null);
  const [loadingUpsellId, setLoadingUpsellId] = useState(null);
  const [error, setError] = useState(null);

  const isEligible =
    !subscription.gift &&
    Boolean(subscription.next_dispatch) &&
    ELIGIBLE_STATUSES.includes(subscription.status);

  useEffect(() => {
    let isCurrent = true;

    if (!isEligible) {
      setUpsells([]);
      return () => {
        isCurrent = false;
      };
    }

    dispatch(getSubscriptionUpsells(subscription.id)).then(
      (availableUpsells) => {
        if (isCurrent) {
          setUpsells(availableUpsells);
        }
      },
      (message) => {
        if (isCurrent) {
          setUpsells([]);
          setError(message);
        }
      }
    );

    return () => {
      isCurrent = false;
    };
  }, [dispatch, isEligible, subscription.id]);

  const addedVariantIds = useMemo(
    () =>
      new Set(
        subscription.products
          .filter((product) => product.type === "onetime")
          .map((product) => product.variant?.id)
          .filter(Boolean)
      ),
    [subscription.products]
  );

  if (!upsells?.length) {
    return (
      <ErrorMessageModal error={error} handleClose={() => setError(null)} />
    );
  }

  const handleAdd = (upsell) => {
    setLoadingUpsellId(upsell.id);
    setError(null);

    dispatch(addSubscriptionUpsellProduct(subscription.id, upsell.id)).then(
      () => {
        setLoadingUpsellId(null);
      },
      (message) => {
        setLoadingUpsellId(null);
        setError(message);
      }
    );
  };

  return (
    <section className={styles.section} aria-labelledby="subscription-upsells">
      <h2 id="subscription-upsells" className="gkit-p-20 fw-bold mb-3">
        Add to your order
      </h2>
      <div
        className={styles.cards}
        role="list"
        aria-label="Products available to add to your next order"
      >
        {upsells.map((upsell) => {
          const isAdded = addedVariantIds.has(upsell.variant?.id);

          return (
            <SubscriptionUpsellCard
              className={styles.card}
              countryCode={subscription.country_code}
              currency={subscription.currency}
              isAdded={isAdded}
              isLoading={loadingUpsellId === upsell.id}
              key={upsell.id}
              onAdd={() => handleAdd(upsell)}
              upsell={upsell}
            />
          );
        })}
      </div>
      <ErrorMessageModal error={error} handleClose={() => setError(null)} />
    </section>
  );
};

export default SubscriptionUpsells;
